/**
 * **Legality composition** — the ONE place `@lancer-vtt/ai` re-runs the exact `checkX`
 * predicate ladder `backend/src/encounterRoom.ts` gates a `request_attack`/`request_move`
 * on, so every generated macro-move passes the same `checkX` predicates the room gates on and
 * is provably executable verbatim.
 *
 * **Never re-derives a rule** — every check below is a straight call into an already-exported
 * `@lancer-vtt/rules`/`@lancer-vtt/domain` predicate, composed in the SAME order the room's
 * `resolveIntent` runs them (see that file's non-reaction `request_attack` branch). Three things
 * this module intentionally does NOT model:
 *   - **Reactions** (Overwatch/Brace) — `checkAttackLegal` rejects a `reaction: true` intent
 *     outright. Reactions are enumerable, so the right extension reads the checkpoint/reaction
 *     registries directly rather than re-deriving `checkOverwatchRange`'s ladder here.
 *   - **Mid-attack locks** (Barrage continuations / Skirmish Aux-riders) — `checkAttackLegal`
 *     and `checkBarrageLegal` require no lock is open; the generator never proposes a
 *     lock-continuation shot.
 *   - **Tech attacks / prepare-release / multi-weapon Barrage** — macro-moves are plain weapon
 *     attacks (loadout weapons + the built-in melee actions Ram/Grapple/Improvised Attack);
 *     `request_activate` tech options (Scan/Lock On/Bolster/Invade) and a two-mount Barrage are an
 *     undone extension. The one Barrage modeled is a **Superheavy's** ({@link checkBarrageLegal}):
 *     a Superheavy fires only in a Barrage (Core p.70) and fires alone, so it is one shot, and the
 *     referee auto-closes its lock.
 */

import {
  activeAttack,
  hasStatus,
  HIDDEN_STATUS,
  mayPlayMove,
  reachableForCombatant,
  worldFor,
  type Cell,
  type EncounterState,
} from "@lancer-vtt/domain";
import {
  actionIdForIntent,
  checkActionUseLegality,
  checkActorStatusLegality,
  checkAttackCellRange,
  checkAttackRange,
  checkAttackWeapon,
  checkAttackCellLoS,
  checkAttackLoS,
  checkBarrageCommit,
  checkBarrageOpener,
  checkBudget,
  checkLimited,
  checkWeaponMode,
  checkSuperheavySkirmish,
  checkLoaded,
  checkOrdnance,
  checkOrdnanceEngagement,
  checkRamSize,
  checkTechRange,
  checkWeaponRecharge,
  intentCost,
} from "@lancer-vtt/rules";
import type { BudgetVerdict } from "@lancer-vtt/rules";
import { err, ok, type RequestAttack, type RequestBarrage, type Result } from "@lancer-vtt/shared";

/**
 * The full non-reaction `request_attack` legality ladder, composed in the same order
 * `encounterRoom.ts`'s `resolveIntent` runs it. Returns the first failing verdict (an
 * `err(reason)`), or `ok(undefined)` iff every gate the room would run also passes.
 */
export function checkAttackLegal(state: EncounterState, intent: RequestAttack): BudgetVerdict {
  if (intent.reaction) {
    return err("ai: reaction attacks (Overwatch) are not generated — Phase 1 reads the checkpoint/reaction registries directly in a later phase, never this ladder");
  }
  if (activeAttack(state.activation) !== null) {
    return err("ai: an open attack lock (Barrage/Aux-rider) is not modeled — no lock-continuation macro-moves are generated");
  }

  const cost = intentCost(intent, undefined, state);
  const budget = checkBudget(state, cost);
  if (!budget.ok) return budget;

  const actionId = actionIdForIntent(intent, { state });
  const actionUse = checkActionUseLegality({ state, intent, actor: cost.actor });
  if (!actionUse.ok) return actionUse;

  const statusVerdict = checkActorStatusLegality({
    state,
    actor: intent.attacker,
    isAttack: true,
    weapon: intent.weapon,
    tech: intent.tech,
    actionId,
  });
  if (!statusVerdict.ok) return statusVerdict;

  if (!intent.tech) {
    const weaponVerdict = checkAttackWeapon({ state, attacker: intent.attacker, weapon: intent.weapon });
    if (!weaponVerdict.ok) return weaponVerdict;
    if (actionId === "skirmish") {
      const superheavy = checkSuperheavySkirmish({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, verb: "skirmish" });
      if (!superheavy.ok) return superheavy;
    }
  }

  const rangeVerdict = intent.tech
    ? checkTechRange({ state, attacker: intent.attacker, target: intent.target })
    : intent.target.kind === "unit"
      ? checkAttackRange({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile })
      : checkAttackCellRange({ state, attacker: intent.attacker, cell: intent.target.cell as Cell, weapon: intent.weapon, profile: intent.profile });
  if (!rangeVerdict.ok) return rangeVerdict;

  if (!intent.tech) {
    const rechargeVerdict = checkWeaponRecharge({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!rechargeVerdict.ok) return rechargeVerdict;

    const loadedVerdict = checkLoaded({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!loadedVerdict.ok) return loadedVerdict;

    const limitedVerdict = checkLimited({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!limitedVerdict.ok) return limitedVerdict;

    const modeVerdict = checkWeaponMode({ state, attacker: intent.attacker, intent });
    if (!modeVerdict.ok) return modeVerdict;

    const ordnanceVerdict = checkOrdnance({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!ordnanceVerdict.ok) return ordnanceVerdict;

    if (intent.target.kind === "unit") {
      const ordnanceEngagement = checkOrdnanceEngagement({
        state,
        attacker: intent.attacker,
        target: intent.target.id,
        weapon: intent.weapon,
        profile: intent.profile,
      });
      if (!ordnanceEngagement.ok) return ordnanceEngagement;
    }
  }

  if (intent.target.kind === "unit") {
    const tgt = state.combatants[intent.target.id];
    if (tgt && hasStatus(tgt.statuses, HIDDEN_STATUS)) {
      return err(`${tgt.name} is Hidden — can't be targeted directly (area attacks still reach it; the GM can override it)`);
    }
    if (intent.weapon === "act_ram") {
      const ramVerdict = checkRamSize({ state, attacker: intent.attacker, target: intent.target.id });
      if (!ramVerdict.ok) return ramVerdict;
    }
  }

  return ok(undefined);
}

/**
 * The **Barrage opener** legality ladder for a lone Superheavy shot, composed in the order
 * `refereeIntent` runs its `request_barrage` gates (budget, once-per-turn use, actor status, then
 * the opener/commit walls, range, line of sight and the per-weapon walls). Returns the first
 * failing verdict, or `ok(undefined)` iff the room would accept `intent` as a Barrage opener.
 * Hidden targets are refused too, stricter than the room's Barrage branch, so the AI never aims at
 * a unit it could not Skirmish.
 */
export function checkBarrageLegal(state: EncounterState, intent: RequestBarrage): BudgetVerdict {
  if (activeAttack(state.activation) !== null) {
    return err("ai: an open attack lock (Barrage/Aux-rider) is not modeled — no lock-continuation macro-moves are generated");
  }

  const cost = intentCost(intent, undefined, state);
  const budget = checkBudget(state, cost);
  if (!budget.ok) return budget;

  const actionId = actionIdForIntent(intent, { state });
  const actionUse = checkActionUseLegality({ state, intent, actor: cost.actor });
  if (!actionUse.ok) return actionUse;

  const statusVerdict = checkActorStatusLegality({ state, actor: intent.attacker, isAttack: true, weapon: intent.weapon, actionId });
  if (!statusVerdict.ok) return statusVerdict;

  if (intent.mounts === undefined) return err("ai: a Barrage opener names its committed mounts");
  const opener = checkBarrageOpener(state, intent.attacker, intent.weapon);
  if (!opener.ok) return opener;
  const commit = checkBarrageCommit({ state, attackerId: intent.attacker, weapon: intent.weapon, mounts: intent.mounts });
  if (!commit.ok) return commit;

  const shot = { state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile };
  if (intent.target.kind === "unit") {
    const range = checkAttackRange({ ...shot, target: intent.target.id });
    if (!range.ok) return range;
    const los = checkAttackLoS({ ...shot, target: intent.target.id });
    if (!los.ok) return los;
  } else {
    const range = checkAttackCellRange({ ...shot, cell: intent.target.cell as Cell });
    if (!range.ok) return range;
    const los = checkAttackCellLoS({ ...shot, cell: intent.target.cell as Cell });
    if (!los.ok) return los;
  }

  const recharge = checkWeaponRecharge(shot);
  if (!recharge.ok) return recharge;
  const limited = checkLimited(shot);
  if (!limited.ok) return limited;
  const ordnance = checkOrdnance(shot);
  if (!ordnance.ok) return ordnance;

  if (intent.target.kind === "unit") {
    const ordnanceEngagement = checkOrdnanceEngagement({ ...shot, target: intent.target.id });
    if (!ordnanceEngagement.ok) return ordnanceEngagement;
    const tgt = state.combatants[intent.target.id];
    if (tgt && hasStatus(tgt.statuses, HIDDEN_STATUS)) {
      return err(`${tgt.name} is Hidden — can't be targeted directly (area attacks still reach it; the GM can override it)`);
    }
  }

  return ok(undefined);
}

/**
 * Is `actorId` legally free to declare a PLAY move to `to` right now — the same wall
 * `move_token`/`request_move` enforce ({@link mayPlayMove} ∧ within {@link reachableForCombatant})?
 * The generator draws its destinations FROM `reachableForCombatant`, so every generated move
 * is legal by construction; this standalone checker exists for the "known-illegal fixtures are
 * never generated" proof (tests feed an out-of-budget/out-of-turn/unreachable cell and assert
 * rejection here matches the generator's own silence).
 */
export function checkMoveLegal(state: EncounterState, actorId: string, to: Cell): Result<void> {
  const actor = state.combatants[actorId];
  if (!actor) return err(`ai: ${actorId} is not on the map`);
  if (!mayPlayMove(state, actor)) {
    return err(`ai: ${actorId} may not move right now (not its activation, or a status bars voluntary movement)`);
  }
  if (actor.pos == null) return err(`ai: ${actorId} is unplaced — nothing to move from`);
  // `reachableForCombatant` itself returns `null` when the remaining move budget is `<= 0`
  // (its own doc) — no separate budget read needed here.
  const reachable = reachableForCombatant(state, actor);
  if (!reachable) return err(`ai: ${actorId} has no reach this turn (no move budget left)`);
  const world = worldFor(state);
  if (!reachable.has(world.key(to))) return err(`ai: ${world.key(to)} is not within ${actorId}'s reach this turn`);
  return ok(undefined);
}
